/**
 * The SVG sprite and provider marks lifted from design-prototype/index.html.
 *
 * The prototype declares its icons once in a hidden <svg> and references them
 * with <use href="#name">. That is kept rather than switching to an icon
 * package: the CSS in src/styles sizes and colours `.icon`, and every rule
 * that does so would have to be rewritten for a different markup shape.
 */

export type IconName =
  | "arrow"
  | "check"
  | "chevron"
  | "chevron-down"
  | "chevrons"
  | "book"
  | "download"
  | "filter"
  | "grid"
  | "list"
  | "lock"
  | "menu"
  | "repo"
  | "settings"
  | "terminal";

export function Icon({ name, className }: { name: IconName; className?: string }) {
  return (
    <svg className={className ? `icon ${className}` : "icon"} aria-hidden="true">
      <use href={`#${name}`} />
    </svg>
  );
}

/**
 * Rendered once, near the root. Every <Icon> references a symbol from here.
 */
export function IconSprite() {
  return (
    <svg width="0" height="0" className="svg-sprite" aria-hidden="true">
      <symbol id="list" viewBox="0 0 24 24">
        <path d="M9 6h12M9 12h12M9 18h12M3 6h.01M3 12h.01M3 18h.01" />
      </symbol>
      <symbol id="filter" viewBox="0 0 24 24">
        <path d="M4 5h16l-6 7v5l-4 2v-7L4 5Z" />
      </symbol>
      <symbol id="download" viewBox="0 0 24 24">
        <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
      </symbol>
      <symbol id="grid" viewBox="0 0 24 24">
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" />
      </symbol>
      <symbol id="repo" viewBox="0 0 24 24">
        <path d="M5 19.5A2.5 2.5 0 0 1 7.5 17H20" />
        <path d="M7.5 3H20v18H7.5A2.5 2.5 0 0 1 5 18.5v-13A2.5 2.5 0 0 1 7.5 3Z" />
        <path d="M9 7h7M9 11h5" />
      </symbol>
      <symbol id="book" viewBox="0 0 24 24">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V4H6.5A2.5 2.5 0 0 0 4 6.5v13Z" />
        <path d="M8 7h8M8 11h6" />
      </symbol>
      <symbol id="settings" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3A1.7 1.7 0 0 0 10 3V2.8h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z" />
      </symbol>
      <symbol id="chevron" viewBox="0 0 24 24">
        <path d="m9 6 6 6-6 6" />
      </symbol>
      <symbol id="chevron-down" viewBox="0 0 24 24">
        <path d="m7 9 5 5 5-5" />
      </symbol>
      <symbol id="chevrons" viewBox="0 0 24 24">
        <path d="m8 9 4-4 4 4M16 15l-4 4-4-4" />
      </symbol>
      <symbol id="menu" viewBox="0 0 24 24">
        <path d="M4 7h16M4 12h16M4 17h16" />
      </symbol>
      <symbol id="arrow" viewBox="0 0 24 24">
        <path d="M5 12h14m-5-5 5 5-5 5" />
      </symbol>
      <symbol id="check" viewBox="0 0 24 24">
        <path d="m5 12 4 4L19 6" />
      </symbol>
      <symbol id="terminal" viewBox="0 0 24 24">
        <path d="m5 7 5 5-5 5M12 17h7" />
      </symbol>
      <symbol id="lock" viewBox="0 0 24 24">
        <rect x="5" y="10" width="14" height="10" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v2" />
      </symbol>
    </svg>
  );
}

/** GitHub wordmark, from the prototype's login screen. */
export function GitHubMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 2a10 10 0 0 0-3.16 19.49c.5.09.68-.22.68-.48v-1.87c-2.78.6-3.37-1.18-3.37-1.18-.45-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.61.07-.61 1 .07 1.53 1.03 1.53 1.03.9 1.53 2.35 1.09 2.92.83.09-.65.35-1.09.64-1.34-2.22-.25-4.56-1.11-4.56-4.94 0-1.09.39-1.98 1.03-2.68-.1-.25-.45-1.27.1-2.64 0 0 .84-.27 2.75 1.02A9.58 9.58 0 0 1 12 6.82a9.5 9.5 0 0 1 2.5.34c1.91-1.29 2.75-1.02 2.75-1.02.55 1.37.2 2.39.1 2.64.64.7 1.03 1.59 1.03 2.68 0 3.84-2.34 4.68-4.57 4.93.36.31.68.92.68 1.85V21c0 .27.18.58.69.48A10 10 0 0 0 12 2Z"
      />
    </svg>
  );
}

/** Google mark, from the prototype's login screen. */
export function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M21.35 12.18c0-.71-.06-1.24-.19-1.8H12v3.43h5.38a4.63 4.63 0 0 1-2 2.95v2.22h3.22c1.89-1.74 2.75-4.3 2.75-6.8Z"
      />
      <path
        fill="#34A853"
        d="M12 21.7c2.7 0 4.96-.89 6.6-2.72l-3.22-2.22c-.9.6-2.04.96-3.38.96-2.6 0-4.8-1.76-5.6-4.12H3.08v2.3A9.97 9.97 0 0 0 12 21.7Z"
      />
      <path
        fill="#FBBC05"
        d="M6.4 13.6A5.99 5.99 0 0 1 6.09 12c0-.56.1-1.1.3-1.6V8.1H3.08A9.75 9.75 0 0 0 2 12c0 1.4.39 2.72 1.08 3.9l3.32-2.3Z"
      />
      <path
        fill="#EA4335"
        d="M12 6.28c1.47 0 2.78.5 3.82 1.5l2.85-2.85A9.6 9.6 0 0 0 12 2.3a9.97 9.97 0 0 0-8.92 5.8l3.32 2.3A5.97 5.97 0 0 1 12 6.28Z"
      />
    </svg>
  );
}
